import React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useExtractionSummary } from '@/services/dashboardService';
import { formatCurrency } from '@/utils/formatters';

/**
 * Dashboard card for AI document extraction (#24, spec 6.7): how many extractions wait for review,
 * how many failed and are still unresolved, this month's extraction spend (shown to every role, like
 * the other money figures), and links straight to the newest pending reviews.
 */
export const DocumentReviewsCard: React.FC = () => {
  const { data, isLoading, error } = useExtractionSummary();

  return (
    <Card className="mb-8" data-testid="document-reviews-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Document reviews</CardTitle>
            <CardDescription>Values read from uploaded documents, waiting for someone to check them</CardDescription>
          </div>
          <Link
            to="/contracts"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline whitespace-nowrap"
          >
            Contracts
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[120px] w-full" />
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>Failed to load document reviews: {error.message}</AlertDescription>
          </Alert>
        ) : data ? (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div>
                <dt className="text-xs text-grey-500">Waiting for review</dt>
                <dd className="text-xl font-semibold text-grey-800" data-testid="pending-review-count">
                  {data.pendingReviewCount}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-grey-500">Failed</dt>
                <dd className="text-xl font-semibold text-grey-800">{data.failedCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-grey-500">Extraction spend this month</dt>
                <dd className="text-xl font-semibold text-grey-800">
                  {formatCurrency(data.monthSpendUsd, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
              </div>
            </dl>
            {data.pendingReviews.length > 0 ? (
              <ul className="divide-y divide-border">
                {data.pendingReviews.map((review) => (
                  <li key={review.extractionId} className="py-2">
                    <Link
                      to="/contracts/$id/documents/$documentId/review"
                      params={{ id: review.contractId, documentId: review.documentId }}
                      className="flex items-start gap-2 text-sm hover:underline"
                    >
                      <ClipboardCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                      <span className="min-w-0 break-words">
                        <span className="font-medium text-grey-800">{review.contractNumber}</span>{' '}
                        <span className="text-grey-600">{review.contractTitle}</span>
                        <span className="block text-xs text-grey-500 break-all">{review.documentFileName}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-grey-500">Nothing waiting for review.</p>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
